<template>
    <div class="emoji-picker">
        <input
            type="text"
            placeholder="Search emoji"
            ref="searchInput"
            v-model="searchValue">

        <!-- CATEGORIES -->
        <ul
            class="emoji-category-list"
            ref="emojiCategories"
            @scroll="onScroll()">

            <li
                class="emoji-category"
                :key="emojiRenderGroup.key"
                v-for="emojiRenderGroup in emojiRenderGroups">

                <div
                    class="emoji-category-title"
                    v-if="emojiRenderGroup.title">
                    {{ emojiRenderGroup.title }}
                </div>

                <!-- EMOJIS -->
                <div class="emoji-list">
                    <!--
                        Originally, all of the "correct" elements were used: a
                        ul to contain the emojis, a button element inside each
                        li, and an Emoji component (or img) within each button.

                        However, since we have hundreds, or even thousands, of
                        emojis to render, each of those extra elements chips
                        away at the performance. Opting for a bunch of buttons
                        with background images shaved nearly 300-400ms off of
                        the initial load time in my (relatively informal) tests.
                    -->
                    <button
                        ref="emojiLi"
                        :key="emoji.value"
                        :data-emoji-value="emoji.value"
                        :style="getButtonStyle(emoji)"
                        :title="emoji.label"
                        @click="pickEmoji(emoji)"
                        v-for="emoji in emojiRenderGroup.emoji">
                    </button>
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
    import EmojiUtils from 'src/utils/emoji';
    import JobUtils from 'src/utils/jobs';


    function getHeight(elem) {
        return elem.offsetHeight; // includes padding and border
    }

    function getOffsetTop(elem) {
        return elem.offsetTop; // relative to nearest 'positioned' parent
    }

    function getScrollTop(elem) {
        return elem.scrollTop;
    }

    async function sleep(ms) {
        await new Promise(r => setTimeout(r, ms));
    }

    class EmojiRenderGroup {
        constructor(params) {
            this.key   = params.key;   // string
            this.title = params.title; // string || null
            this.emoji = params.emoji; // EmojiUtils.EmojiInfo[]
        }
    }

    // Simple helper to help sleep occasionally when processing large lists.
    // Once per loop:
    //  1) call tick()
    //  2) check if shouldSleep()
    //  3) if should sleep, await sleep()
    class SleepTicker {
        constructor(ticksPerSleep, sleepAmount) {
            this.ticksPerSleep = ticksPerSleep;
            this.sleepAmount = sleepAmount;
            this.tickAccum = 0;
        }

        tick() {
            this.tickAccum++;
        }

        shouldSleep() {
            return this.tickAccum >= this.ticksPerSleep;
        }

        async sleep() {
            await sleep(this.sleepAmount);
            this.tickAccum = 0;
        }
    }


    export default {
        data() {
            return {
                searchValue: '',

                // The following info is used to check which emoji need to be
                // rendered (off-screen emojis aren't rendered at first to avoid
                // a slew of network requests when first opening the emoji
                // picker).
                lazyLoadInfo: {
                    emojiLoaded: {
                        // [emoji.value]: boolean,
                    },
                    emojiLiTop: {
                        // [emoji.value]: number,
                        // unloaded emoji only
                    },
                    emojiLiTop_arr: [
                        // [array index]: { value, top }
                        // unloaded emoji only
                        // sorted by top
                    ],
                },

                updateLoadedEmojiList_job: new JobUtils.Job(
                    () => this.updateLoadedEmojiList(),
                    {
                        rateLimitPeriod: 250,
                        rateLimitFollowUp: true,
                        parallelRuns: false,
                        parallelRunFollowUp: true,
                    },
                ),
                updateEmojiTopList_job: new JobUtils.Job(
                    () => this.updateEmojiTopList(),
                    {
                        rateLimitPeriod: 500,
                        rateLimitFollowUp: true,
                        parallelRuns: false,
                        parallelRunFollowUp: true,
                    },
                ),
            };
        },

        mounted() {
            this.resetLazyLoadInfo();
        },

        methods: {
            onScroll() {
                this.updateLoadedEmojiList_job.trigger();
            },

            focus() {
                this.$refs.searchInput.focus();
            },

            pickEmoji(emoji) {
                this.$emit('pick', emoji.value);
            },

            resetLazyLoadInfo() {
                this.lazyLoadInfo.emojiLoaded = {};
                this.lazyLoadInfo.emojiLiTop = {};
                this.lazyLoadInfo.emojiLiTop_arr = [];
                (async () => {
                    await this.updateEmojiTopList_job.trigger();
                    await this.updateLoadedEmojiList_job.trigger();
                })();
            },

            shouldRenderEmoji(emoji) {
                return this.lazyLoadInfo.emojiLoaded[emoji.value];
            },

            async updateLoadedEmojiList() {
                // Calculate the maximum offset that's within view
                const emojiCategoriesRef = this.$refs.emojiCategories;
                const maxViewY = (
                    getHeight(emojiCategoriesRef) +
                    getScrollTop(emojiCategoriesRef)
                );

                // Check all unloaded emoji
                const sleepTicker = new SleepTicker(50, 1);

                const emojiLiTop_arr = this.lazyLoadInfo.emojiLiTop_arr;
                for (let i = 0; i < emojiLiTop_arr.length; ++i) {
                    const emojiValue = emojiLiTop_arr[i].value;
                    const top = emojiLiTop_arr[i].top;

                    // Sleep occasionally to avoid stutters
                    sleepTicker.tick();
                    if (sleepTicker.shouldSleep()) {
                        await sleepTicker.sleep();
                    }

                    // Check if the top of the emoji li is within view
                    if (top <= maxViewY &&
                        !this.lazyLoadInfo.emojiLoaded[emojiValue]) {
                        this.$set(
                            this.lazyLoadInfo.emojiLoaded,
                            emojiValue,
                            true
                        );

                    } else {
                        // The tops were sorted when building the list, so as
                        // soon as one top is out of view, the rest can be
                        // skipped.
                        break;
                    }
                }

                // Optimization: delete any top values for emojis that are now
                // marked as loaded, since we don't need to track those anymore.
                // Without this optimization, they will continue to be checked
                // while the user scrolls until the tops are recalculated.
                for (let i = 0; i < emojiLiTop_arr.length; ++i) {
                    const emojiValue = emojiLiTop_arr[i].value;
                    if (this.lazyLoadInfo.emojiLoaded[emojiValue]) {
                        this.$delete(this.lazyLoadInfo.emojiLiTop, emojiValue);
                    } else {
                        this.lazyLoadInfo.emojiLiTop_arr.splice(0, i);
                        break;
                    }
                }
            },

            async updateEmojiTopList() {
                const emojiLiTop = {};

                // Collect tops for unloaded emoji
                const sleepTicker = new SleepTicker(50, 1);
                for (const emojiLiRef of this.$refs.emojiLi) {
                    const emojiValue = emojiLiRef.dataset.emojiValue;

                    // Sleep occasionally to avoid stutters
                    sleepTicker.tick();
                    if (sleepTicker.shouldSleep()) {
                        await sleepTicker.sleep();
                    }

                    // If emoji is not loaded currently, update the top; in the
                    // case when an emoji has more than one associated li (like
                    // if it is in a "Recently used" group and another), take
                    // the smallest value.
                    if (!this.lazyLoadInfo.emojiLoaded[emojiValue]) {
                        const newTop = getOffsetTop(emojiLiRef);
                        const oldTop = emojiLiTop[emojiValue];
                        if (oldTop === undefined || oldTop > newTop) {
                            emojiLiTop[emojiValue] = newTop;
                        }
                    }
                }

                // Sort tops so we can early out in updateEmojiLoadedList()
                const emojiLiTop_arr = Object.entries(emojiLiTop).sort(
                    ([aKey, aTop], [bKey, bTop]) => {
                        return aTop - bTop;
                    }
                ).map(([aKey, aTop]) => {
                    return {
                        value: aKey,
                        top: aTop,
                    };
                });

                this.lazyLoadInfo.emojiLiTop = emojiLiTop;
                this.lazyLoadInfo.emojiLiTop_arr = emojiLiTop_arr;
            },

            getButtonStyle(emoji) {
                let url;
                if (this.shouldRenderEmoji(emoji)) {
                    url = emoji.url;
                }
                return {
                    'background-image': url ? `url("${url}")` : '',
                };
            },
        },

        computed: {
            searchResultEmojis() {
                function cmp(s1, s2) {
                    s1 = s1.toLowerCase().trim();
                    s2 = s2.toLowerCase().trim();
                    return s1.includes(s2) || s2.includes(s1);
                }
                const sv = this.searchValue;
                return EmojiUtils.getAllEmoji().filter(emoji => {
                    return (cmp(sv, emoji.value) ||
                            cmp(sv, emoji.shortname) ||
                            cmp(sv, emoji.label));
                });
            },

            emojiRenderGroups() {
                if (this.searchValue) {
                    return [
                        new EmojiRenderGroup({
                            key: this.searchValue,
                            title: null,
                            emoji: this.searchResultEmojis,
                        }),
                    ];
                } else {
                    return EmojiUtils.getCategories().map(cat => {
                        const key = cat.id;
                        const title = cat.label;
                        const emoji = EmojiUtils.getAllEmojiByCategory(cat.id);
                        return new EmojiRenderGroup({ key, title, emoji });
                    });
                }
            },
        },

        watch: {
            emojiRenderGroups: function() {
                // Wait for the html elements to update based on the new emoji
                // render groups before updating the emoji top list
                this.$nextTick(() => {
                    this.resetLazyLoadInfo();
                });
            },
        },
    }
</script>

<style lang="scss" scoped>
    .emoji-picker {
        white-space: nowrap;
        overflow: hidden;
        background: #363636;
        border: 1px solid #666;
        //min-height: 100px;
        //max-height: 400px;
        display: flex;
        flex-direction: column;
    }

    input {
        background: none;
        border: none;
        border-bottom: 1px solid #555;
        width: 100%;
        padding: 8px;
        color: #f0f0f0;

        &:focus {
            outline: none;
        }
    }

    .emoji-category-list {
        margin: 0;
        padding: 10px;
        list-style: none;
        overflow: auto;
        flex: 1;
        position: relative;
    }

    .emoji-category {
        &:not(:first-of-type) {
            .emoji-category-title {
                margin-top: 20px;
            }
        }
    }

    .emoji-category-title {
        font-weight: bold;
        margin-bottom: 10px;
    }

    .emoji-list {
        display: flex;
        flex-wrap: wrap;

        button {
            background: transparent center / 20px 20px no-repeat;
            border: none;
            width: 28px;
            height: 28px;

            &:hover {
                background-color: #555;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// index.vue?796a853e